/* Report starter templates. Each builds a valid schemaVersion-1 definition
 * on the 24-column TV grid (canvas 1848x944, rowHeight 20, gap 8 -> about 33
 * rows). Widgets start unbound (datasetId null): the designer shows them as
 * "choose data" placeholders so the layout is ready and only the data needs
 * picking. */

type W = { id: string; type: string; title: string; x: number; y: number; w: number; h: number; extra?: Record<string, unknown> };

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  accent: string;
  pages: Array<{ name: string; widgets: W[] }>;
}

let seq = 0;
const wid = (p: string) => `${p}-${(++seq).toString(36)}`;

function kpiRow(y: number, titles: string[]): W[] {
  const w = Math.floor(24 / titles.length);
  return titles.map((t, i) => ({ id: wid("kpi"), type: "kpi", title: t, x: i * w, y, w: i === titles.length - 1 ? 24 - i * w : w, h: 5 }));
}

export const TEMPLATES: ReportTemplate[] = [
  {
    id: "blank",
    name: "Blank report",
    description: "One empty page on the TV grid. Add visuals from the palette.",
    accent: "#64748b",
    pages: [{ name: "Overview", widgets: [] }],
  },
  {
    id: "executive",
    name: "Executive overview",
    description: "Four headline KPIs, a monthly trend, a breakdown and a detail table — the classic board-room page.",
    accent: "#0f6cbd",
    pages: [
      {
        name: "Overview",
        widgets: [
          ...kpiRow(0, ["Revenue", "Cost", "Margin", "Utilization"]),
          { id: wid("trend"), type: "line_chart", title: "Monthly trend", x: 0, y: 5, w: 15, h: 14 },
          { id: wid("mix"), type: "donut_chart", title: "Breakdown", x: 15, y: 5, w: 9, h: 14 },
          { id: wid("tbl"), type: "table", title: "Details", x: 0, y: 19, w: 24, h: 14 },
        ],
      },
    ],
  },
  {
    id: "finance",
    name: "Finance & cost",
    description: "Budget vs actual combo chart, cost split by category and a monthly cost table.",
    accent: "#2e9d68",
    pages: [
      {
        name: "Cost overview",
        widgets: [
          { id: wid("f"), type: "filter", title: "Year", x: 0, y: 0, w: 4, h: 3 },
          { id: wid("f"), type: "filter", title: "Month", x: 4, y: 0, w: 4, h: 3 },
          ...kpiRow(3, ["Budget", "Actual", "Variance"]),
          { id: wid("combo"), type: "combo_chart", title: "Budget vs actual", x: 0, y: 8, w: 16, h: 13 },
          { id: wid("mix"), type: "donut_chart", title: "Cost by category", x: 16, y: 8, w: 8, h: 13 },
          { id: wid("tbl"), type: "table", title: "Monthly cost", x: 0, y: 21, w: 24, h: 12 },
        ],
      },
    ],
  },
  {
    id: "operations",
    name: "Fleet & operations",
    description: "Vessel status at a glance: utilization KPIs, status by vessel, and an activity list.",
    accent: "#18a8c9",
    pages: [
      {
        name: "Fleet status",
        widgets: [
          ...kpiRow(0, ["Vessels on hire", "Off hire", "Utilization %", "Open jobs"]),
          { id: wid("bar"), type: "bar_chart", title: "Utilization by vessel", x: 0, y: 5, w: 14, h: 15 },
          { id: wid("st"), type: "status_list", title: "Vessel status", x: 14, y: 5, w: 10, h: 15 },
          { id: wid("tbl"), type: "table", title: "Activity log", x: 0, y: 20, w: 24, h: 13 },
        ],
      },
    ],
  },
  {
    id: "hr",
    name: "People & HR",
    description: "Headcount, hiring and turnover with department split and a positions table.",
    accent: "#a855f7",
    pages: [
      {
        name: "Workforce",
        widgets: [
          ...kpiRow(0, ["Headcount", "New hires", "Leavers", "Open positions"]),
          { id: wid("bar"), type: "bar_chart", title: "Headcount by department", x: 0, y: 5, w: 14, h: 14 },
          { id: wid("mix"), type: "pie_chart", title: "Gender split", x: 14, y: 5, w: 10, h: 14 },
          { id: wid("tbl"), type: "table", title: "Open positions", x: 0, y: 19, w: 24, h: 14 },
        ],
      },
    ],
  },
  {
    id: "claims",
    name: "Claims & insurance",
    description: "Claims tracker with filters, totals by status, and a searchable claims table.",
    accent: "#d9911a",
    pages: [
      {
        name: "Claims",
        widgets: [
          { id: wid("f"), type: "filter", title: "Policy", x: 0, y: 0, w: 5, h: 3 },
          { id: wid("f"), type: "filter", title: "Status", x: 5, y: 0, w: 5, h: 3 },
          ...kpiRow(3, ["Open claims", "Claimed amount", "Settled amount"]),
          { id: wid("mix"), type: "donut_chart", title: "Claims by status", x: 0, y: 8, w: 9, h: 12 },
          { id: wid("bar"), type: "bar_chart", title: "Claims by month", x: 9, y: 8, w: 15, h: 12 },
          { id: wid("tbl"), type: "table", title: "Claims register", x: 0, y: 20, w: 24, h: 13 },
        ],
      },
    ],
  },
];

export function buildDefinition(template: ReportTemplate, reportId: string, name: string, description?: string): Record<string, unknown> {
  const pages = template.pages.map((p, i) => ({
    id: `page-${i + 1}`,
    name: p.name,
    order: i,
    canvas: { width: 1848, height: 944, columns: 24, rowHeight: 20, gap: 8 },
    background: { type: "solid", value: "#0a1628" },
    filters: [],
    widgets: p.widgets.map((w, z) => ({
      id: w.id,
      type: w.type,
      title: w.title,
      layout: { x: w.x, y: w.y, w: w.w, h: w.h, z: z + 1 },
      datasetId: null,
      query: { dimensions: [], measures: [], filters: [], sort: [], limit: 100 },
      display: { showTitle: true, showBorder: true, showLastRefresh: true },
      interaction: { crossFilter: false, drillDown: false, drillTarget: null },
      refreshSeconds: 60,
      fallback: { showLastSuccessfulData: true, message: "Live source unavailable" },
      ...(w.extra ?? {}),
    })),
  }));
  return {
    schemaVersion: 1,
    reportId,
    name,
    description: description ?? "",
    theme: "maridive-dark",
    refreshSeconds: 60,
    defaultPageId: "page-1",
    settings: { showLastRefresh: true, showDataStatus: true, enablePageRotation: false, pageRotationSeconds: 30, allowViewerFilters: true },
    globalFilters: [],
    pages,
  };
}
